import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FeatureModule } from './feature.model';
import { FeatureService } from './feature.service';
import { SessionService } from './session.service';

/**
 * Guard de ruta: bloquea el acceso a una ruta si el tenant no tiene el módulo,
 * redirigiendo al POS. Complementa a *appFeature (que oculta en la UI): aquí se
 * evita el acceso directo por URL.
 *
 *   { path: 'discounts', canActivate: [featureGuard('descuentos')], ... }
 *
 * Si falta el propio módulo «ventas», el destino NO puede ser `/pos`: esa ruta
 * lleva este mismo guard y el enrutador entraba en un bucle síncrono de
 * redirecciones (/pos → /pos → …) que dejaba la pestaña colgada hasta que
 * Chrome ofrecía «esperar o salir» (medido el 2026-09-10 con una sesión
 * guardada cuyos módulos no traían «ventas»). En ese caso se manda al login.
 *
 * **Sin token, al login** (2026-09-18). Hasta ese día, sin nada guardado el POS se inventaba una sesión «demo» con
 * todos los módulos, y mirar solo los módulos dejaba pasar: un dispositivo nuevo que abría `/pos` entraba a una caja
 * sin negocio detrás, vacía y sin poder vender. La sesión inventada ya no existe, pero se sigue mirando el TOKEN, como
 * en Ruta: es lo que dice que alguien entró. El demo de verdad («Probar el demo», en el login) guarda el suyo.
 */
export function featureGuard(module: FeatureModule): CanActivateFn {
  return () => {
    const features = inject(FeatureService);
    const router = inject(Router);
    if (!inject(SessionService).tieneToken()) {
      return router.createUrlTree(['/login']);
    }
    // Z50 (guía de Bodega 1.4): el bodeguero no vende. Escribir /pos (o cualquier pantalla de caja) lo devuelve a /bodega sin
    // pasos intermedios; antes entraba a una caja que no carga nada y con el recorrido de bienvenida encima.
    if (inject(SessionService).session()?.role === 'bodeguero') {
      return router.createUrlTree(['/bodega']);
    }
    if (features.has(module)) {
      return true;
    }
    return router.createUrlTree([features.has('ventas') ? '/pos' : '/login']);
  };
}
